import { memo } from "react";
import { useI18n } from "@/i18n";
import { colors, spacing, typography } from "@/design-system/tokens";

interface StatItem {
  value: string;
  desc: string;
  icon: React.ReactNode;
  accent?: boolean;
}

export const Stats = memo(function Stats() {
  const { t } = useI18n();

  const items: StatItem[] = [
    {
      value: t.stats.expertise,
      desc: t.stats.expertiseDesc,
      icon: <IconYears />,
    },
    {
      value: t.stats.countries,
      desc: t.stats.countriesDesc,
      icon: <IconGlobe />,
    },
    {
      value: t.stats.customers,
      desc: t.stats.customersDesc,
      icon: <IconCustomers />,
      accent: true,
    },
  ];

  return (
    <section
      aria-label="Key statistics"
      style={{
        background: "#fff",
        padding: `80px ${spacing.xl}`,
      }}
    >
      <div
        style={{
          maxWidth: "860px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "0px",
        }}
        className="stats-grid"
      >
        {items.map((item, i) => (
          <StatRow
            key={item.value}
            {...item}
            index={i}
            total={items.length}
          />
        ))}
      </div>
    </section>
  );
});

const StatRow = memo(function StatRow({
  value,
  desc,
  icon,
  accent = false,
  index,
  total,
}: StatItem & { index: number; total: number }) {
  const widthPercent = 60 + (index / (total - 1)) * 40;

  return (
    <div
      style={{
        width: `${widthPercent}%`,
        background: accent ? colors.primary : "#f4f4f4",
        borderRadius: "16px",
        padding: "28px 36px",
        display: "flex",
        alignItems: "center",
        gap: spacing.xl,
        marginBottom: index < total - 1 ? "-12px" : "0",
        position: "relative",
        zIndex: index + 1,
        boxShadow: accent
          ? "0 8px 32px rgba(235,81,57,0.25)"
          : "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >
      <span
        style={{
          fontFamily: typography.fontFamily,
          fontSize: "clamp(2rem, 4vw, 3rem)",
          fontWeight: typography.weight.bold,
          color: accent ? "#fff" : "#111",
          letterSpacing: "-0.05em",
          lineHeight: 1,
          flexShrink: 0,
          minWidth: "120px",
        }}
      >
        {value}
      </span>

      <p
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.sm,
          color: accent ? "rgba(255,255,255,0.8)" : "#777",
          margin: 0,
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {desc}
      </p>

      <span
        style={{
          color: accent ? "rgba(255,255,255,0.7)" : "#bbb",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
        }}
        aria-hidden="true"
      >
        {icon}
      </span>
    </div>
  );
});

function IconYears() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function IconGlobe() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function IconCustomers() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
